import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { authStore } from '@/stores/auth'
import { isFnaFlavor } from '@/config/appFlavor'
import { pendingNotifyTarget } from '@/composables/useNotifyDeepLink'
// 스플래시·엔트리는 lazy chunk 실패 시 앱이 영원히 로딩에 남으므로 동기 로드
import SplashPage from '@/pages/SplashPage.vue'
import EntryPage from '@/pages/EntryPage.vue'

const publicPaths = new Set([
  '/splash',
  '/entry',
  '/login',
  '/login/center',
  '/login/yobo',
  '/signup',
  '/signup/yobo',
  '/signup/center',
  '/reset-password',
  '/terms',
])

const routes: Array<RouteRecordRaw> = [
  // 문자 링크 query(?source=sms) 유지
  {
    path: '/',
    redirect: (to) => ({ path: '/splash', query: to.query, hash: to.hash }),
  },
  { path: '/splash', name: 'Splash', component: SplashPage },
  { path: '/entry', name: 'Entry', component: EntryPage },
  { path: '/login', name: 'Login', component: () => import('@/pages/auth/LoginPage.vue') },
  { path: '/login/center', redirect: { path: '/login', query: { type: 'center' } } },
  { path: '/login/yobo', redirect: { path: '/login', query: { type: 'yobo' } } },
  { path: '/signup', name: 'Signup', component: () => import('@/pages/auth/SignupPage.vue') },
  { path: '/signup/yobo', redirect: { path: '/signup', query: { type: 'yobo' } } },
  { path: '/signup/center', redirect: { path: '/signup', query: { type: 'center' } } },
  { path: '/reset-password', name: 'ResetPassword', component: () => import('@/pages/auth/ResetPasswordPage.vue') },
  { path: '/terms', name: 'Terms', component: () => import('@/pages/auth/TermsPage.vue') },
  { path: '/owner', name: 'Owner', component: () => import('@/pages/owner/OwnerShell.vue'), meta: { role: 'OWNER' } },
  {
    path: '/owner/accounting',
    name: 'OwnerAccounting',
    component: () => import('@/pages/owner/accounting/OwnerAccountingPage.vue'),
    meta: { role: 'OWNER' },
  },
  {
    path: '/owner/accounting/receipts',
    name: 'OwnerReceipts',
    component: () => import('@/pages/owner/receipts/ReceiptAlbumPage.vue'),
    meta: { role: 'OWNER' },
  },
  {
    path: '/owner/accounting/receipts/scan',
    name: 'OwnerReceiptScan',
    component: () => import('@/pages/owner/receipts/ReceiptScanPage.vue'),
    meta: { role: 'OWNER' },
  },
  {
    path: '/fna',
    name: 'FnaHome',
    component: () => import('@/pages/owner/receipts/ReceiptAlbumPage.vue'),
  },
  {
    path: '/fna/scan',
    name: 'FnaScan',
    component: () => import('@/pages/owner/receipts/ReceiptScanPage.vue'),
  },
  {
    path: '/owner/receipts',
    redirect: '/owner/accounting/receipts',
    meta: { role: 'OWNER' },
  },
  {
    path: '/owner/receipts/scan',
    redirect: '/owner/accounting/receipts/scan',
    meta: { role: 'OWNER' },
  },
  { path: '/sw', name: 'Sw', component: () => import('@/pages/sw/SwShell.vue'), meta: { role: 'SOCIAL_WORKER' } },
  { path: '/caregiver', name: 'Caregiver', component: () => import('@/pages/caregiver/CaregiverShell.vue'), meta: { role: 'CAREGIVER' } },
  {
    path: '/caregiver/center/:mCode/chat',
    name: 'CaregiverCenterChat',
    component: () => import('@/pages/caregiver/center/CaregiverCenterChatPage.vue'),
    meta: { role: 'CAREGIVER' },
  },
  {
    path: '/caregiver/center/:mCode/chat/menu',
    name: 'CaregiverCenterChatMenu',
    component: () => import('@/pages/caregiver/center/CaregiverCenterChatMenuPage.vue'),
    meta: { role: 'CAREGIVER' },
  },
  {
    path: '/caregiver/center/:mCode/chat/docs',
    name: 'CaregiverDocManage',
    component: () => import('@/pages/caregiver/center/CaregiverDocManagePage.vue'),
    meta: { role: 'CAREGIVER' },
  },
  {
    path: '/caregiver/center/:mCode',
    redirect: (to) => {
      const m = String(to.params.mCode || '').trim()
      if (!m || m === 'undefined' || m === 'null') {
        return { path: '/caregiver' }
      }
      return {
        path: `/caregiver/center/${encodeURIComponent(m)}/chat`,
        query: to.query,
      }
    },
  },
  {
    path: '/caregiver/admin-privilege',
    name: 'AdminPrivilege',
    component: () => import('@/pages/caregiver/AdminPrivilegePage.vue'),
    meta: { role: 'CAREGIVER', admin: true },
  },
  {
    path: '/app/support',
    name: 'AppSupport',
    component: () => import('@/pages/app/SupportPage.vue'),
    meta: { appUtil: true },
  },
  {
    path: '/app/notifications',
    name: 'AppNotifications',
    component: () => import('@/pages/app/NotifyInboxPage.vue'),
    meta: { appUtil: true },
  },
  {
    path: '/app/changelog',
    name: 'AppChangelog',
    component: () => import('@/pages/app/AppChangelogPage.vue'),
    meta: { appUtil: true },
  },
  { path: '/app/menu', redirect: '/app/notifications' },
  // 스플래시로 보내면 설치 앱에서 splash↔unknown 루프가 날 수 있음
  { path: '/:pathMatch(.*)*', redirect: '/entry' },
]

/** 라우터 base는 항상 '/' — process.env.BASE_URL 사용 금지
 * (미주입·문자열 "undefined"·HMR 시 …:82undefined/ 로 Location.replace 실패) */
const router = createRouter({
  history: createWebHistory('/'),
  routes,
})

router.beforeEach(async (to, from) => {
  // 잘못된 딥링크·env 잔여로 path에 undefined가 섞이면 즉시 차단
  const bad =
    !to.path ||
    to.path.includes('undefined') ||
    String(to.fullPath || '').includes('undefined') ||
    (typeof to.params.mCode === 'string' && to.params.mCode === 'undefined')
  if (bad && to.path !== '/entry') {
    console.warn('[router] blocked invalid location:', to.fullPath)
    return authStore.state.user ? authStore.homeForRole(authStore.state.user.appRole) : '/entry'
  }

  if (authStore.state.booting && to.path !== '/splash') {
    try {
      await Promise.race([
        authStore.bootstrap(),
        new Promise<void>((resolve) => setTimeout(resolve, 8000)),
      ])
    } catch {
      /* ignore */
    } finally {
      authStore.state.booting = false
    }
  }

  const loggedIn = !!authStore.state.user
  const role = authStore.state.user?.appRole
  const path = to.path

  // 부팅이 아직이면 스플래시에서만 대기 (query 유지 — 문자 source=sms 등)
  if (authStore.state.booting && path !== '/splash') {
    return { path: '/splash', query: to.query, hash: to.hash }
  }

  // PWA/브라우저가 마지막 URL(관리자특권)로 복원한 경우 — 앱 홈으로
  if (
    path === '/caregiver/admin-privilege' &&
    !from.name &&
    from.matched.length === 0
  ) {
    return '/caregiver'
  }

  if (!loggedIn) {
    if (path === '/splash') return true
    if (publicPaths.has(path)) return true
    return { path: '/entry', query: to.query }
  }

  const home = authStore.homeForRole(role)
  const notifyTarget = pendingNotifyTarget()
  const postAuthTarget = notifyTarget || home
  // 이미 로그인된 상태면 로그인·가입·엔트리·스플래시로 보내지 않음 (세션 유지)
  // home === path 이면 true (동일 경로 redirect 루프 방지)
  if (
    path === '/splash' ||
    path === '/entry' ||
    path === '/login' ||
    path.startsWith('/login/') ||
    path.startsWith('/signup')
  ) {
    if (!postAuthTarget || postAuthTarget === path) return true
    return postAuthTarget
  }

  if (role === 'OWNER' && path.startsWith('/caregiver')) return '/owner'
  if (role === 'CAREGIVER' && (path.startsWith('/owner') || path.startsWith('/sw'))) return '/caregiver'

  // fna 앱 — 회계담당·기관(센터장·사회복지사)은 영수증 화면만 사용
  const FNA_RECEIPT_ROLES = ['ACCOUNT', 'OWNER', 'SOCIAL_WORKER']
  if (
    isFnaFlavor() &&
    FNA_RECEIPT_ROLES.includes(String(role || '')) &&
    !path.startsWith('/fna') &&
    path !== '/app/changelog'
  ) {
    return '/fna'
  }
  if (role === 'ACCOUNT' && !path.startsWith('/fna') && path !== '/app/changelog') {
    return '/fna'
  }
  if (path.startsWith('/fna')) {
    const fnaAllowed = isFnaFlavor()
      ? FNA_RECEIPT_ROLES.includes(String(role || ''))
      : role === 'ACCOUNT'
    if (!fnaAllowed) {
      return authStore.homeForRole(role)
    }
  }

  const isAccountingPath =
    path.startsWith('/owner/accounting') || path.startsWith('/owner/receipts')
  const swCanAccounting =
    role === 'SOCIAL_WORKER' && authStore.canUseAccounting.value && isAccountingPath

  if (role === 'SOCIAL_WORKER' && path.startsWith('/owner') && !swCanAccounting) {
    return '/sw'
  }

  if (to.meta?.role && role !== to.meta.role) {
    if (swCanAccounting && to.meta.role === 'OWNER') {
      // 회계 권한 SW → /owner/accounting* 허용
    } else {
      if (!home || home === path) return true
      return home
    }
  }

  if (to.meta?.admin && role === 'CAREGIVER' && !authStore.state.user?.isAdmin) {
    return '/caregiver'
  }

  return true
})

export default router
